import { z } from 'zod';

/**
 * Contratto dei calciatori di un'asta: import da Excel e lettura della rosa.
 *
 * Qui vivono anche i **nomi delle colonne** del file e i limiti di validazione:
 * il browser deve poter offrire lo stesso modello di file e mostrare gli stessi
 * vincoli che il server applica, senza che le due liste divergano.
 *
 * Come per gli altri enum condivisi, `PLAYER_ROLES` è ridichiarato invece di
 * essere importato da Prisma; la parità è verificata in `libs/database`.
 */
export const PLAYER_ROUTES = {
  /** Template Fastify; per un URL concreto usa `auctionPlayersPath()`. */
  collection: '/auctions/:id/players',
  import: '/auctions/:id/players/import',
} as const;

export function auctionPlayersPath(auctionId: string): string {
  return `/auctions/${encodeURIComponent(auctionId)}/players`;
}

export function auctionPlayersImportPath(auctionId: string): string {
  return `${auctionPlayersPath(auctionId)}/import`;
}

// ---------------------------------------------------------------------------
// Ruoli
// ---------------------------------------------------------------------------

export const PLAYER_ROLES = ['P', 'D', 'C', 'A'] as const;

export type PlayerRoleValue = (typeof PLAYER_ROLES)[number];

export const playerRoleSchema = z.enum(PLAYER_ROLES);

/** Conteggio per ruolo. Le chiavi sono sempre tutte e quattro, anche a zero. */
export type PlayerRoleCounts = Readonly<Record<PlayerRoleValue, number>>;

export const EMPTY_ROLE_COUNTS: PlayerRoleCounts = { P: 0, D: 0, C: 0, A: 0 };

/**
 * Etichette dei ruoli, al plurale: è la forma che serve sia ai messaggi
 * dell'API («slot esauriti per il ruolo Difensori») sia alle intestazioni della
 * dashboard. Vivono qui e non nel client perché il server le usa davvero.
 */
export const PLAYER_ROLE_LABELS: Readonly<Record<PlayerRoleValue, string>> = {
  P: 'Portieri',
  D: 'Difensori',
  C: 'Centrocampisti',
  A: 'Attaccanti',
};

// ---------------------------------------------------------------------------
// Formato del file
// ---------------------------------------------------------------------------

/**
 * Intestazioni attese, nell'ordine del capitolato.
 *
 * Il confronto lato server è insensibile a maiuscole e spazi, ma non all'ordine
 * dei dati: ogni colonna è individuata per nome, non per posizione.
 */
export const PLAYERS_IMPORT_COLUMNS = ['id', 'nome', 'squadra', 'ruolo', 'valore'] as const;

export type PlayersImportColumn = (typeof PLAYERS_IMPORT_COLUMNS)[number];

/**
 * Estensioni accettate.
 *
 * Tre formati per la stessa tabella: Open XML (`.xlsx`), il vecchio binario di
 * Excel (`.xls`, ancora quello che molti siti di quotazioni distribuiscono) e
 * il testo separato (`.csv`). L'estensione **non** decide come il file viene
 * letto — quello lo decidono i byte — serve solo a filtrare l'upload e a dare
 * un messaggio comprensibile.
 */
export const PLAYERS_IMPORT_FILE_EXTENSIONS = ['.xlsx', '.xls', '.csv'] as const;

export type PlayersImportFileExtension = (typeof PLAYERS_IMPORT_FILE_EXTENSIONS)[number];

/**
 * `Content-Type` osservati per quei tre formati.
 *
 * L'elenco è lungo perché il tipo dichiarato dal browser dipende dal sistema
 * operativo e dai programmi installati: lo stesso `.csv` arriva come `text/csv`
 * su macOS e come `application/vnd.ms-excel` su una macchina con Excel. È un
 * dato fornito dal client, quindi resta un indizio, mai una verifica.
 */
export const PLAYERS_IMPORT_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/msexcel',
  'application/x-msexcel',
  'text/csv',
  'text/plain',
  'text/comma-separated-values',
  'application/csv',
] as const;

/** Valore dell'attributo `accept` dell'input file, per non riscriverlo nel client. */
export const PLAYERS_IMPORT_ACCEPT = PLAYERS_IMPORT_FILE_EXTENSIONS.join(',');

/** Nome del campo multipart che trasporta il file. */
export const PLAYERS_IMPORT_FIELD = 'file';

/**
 * Limiti del contenuto.
 *
 * `maxRows` è un limite di sanità: una lega ha qualche centinaio di calciatori,
 * un file da centomila righe è quasi certamente il file sbagliato.
 */
export const PLAYERS_IMPORT_LIMITS = {
  maxRows: 5_000,
  externalIdMaxLength: 64,
  nameMaxLength: 120,
  teamMaxLength: 80,
  valueMin: 0,
  valueMax: 10_000,
} as const;

/**
 * Numero massimo di errori riportati.
 *
 * Un file completamente sbagliato produrrebbe un errore per riga: la risposta
 * diventerebbe enorme e illeggibile. Oltre la soglia si tronca e lo si dichiara
 * esplicitamente in `truncated`.
 */
export const PLAYERS_IMPORT_MAX_ISSUES = 100;

// ---------------------------------------------------------------------------
// File di esempio
// ---------------------------------------------------------------------------

/**
 * Riga del file di esempio: una cella per ogni colonna prevista.
 *
 * Il tipo è `Record<PlayersImportColumn, string>`, quindi aggiungere una colonna
 * a `PLAYERS_IMPORT_COLUMNS` rompe la compilazione delle righe qui sotto: il
 * modello offerto in download non può restare indietro rispetto al formato che
 * il server pretende.
 */
export type PlayersImportTemplateRow = Readonly<Record<PlayersImportColumn, string>>;

/**
 * Contenuto del file di esempio.
 *
 * Nomi e squadre sono deliberatamente generici: un modello con le quotazioni
 * vere di una stagione verrebbe caricato così com'è, e sarebbe la lista
 * sbagliata. Due righe per reparto perché il file deve mostrare tutti e quattro
 * i codici di ruolo — è l'unico campo con un vocabolario chiuso, ed è l'errore
 * più frequente.
 */
export const PLAYERS_IMPORT_TEMPLATE_ROWS: readonly PlayersImportTemplateRow[] = [
  { id: '1', nome: 'Rossi', squadra: 'Inter', ruolo: 'P', valore: '13' },
  { id: '2', nome: 'Bianchi', squadra: 'Milan', ruolo: 'P', valore: '9' },
  { id: '3', nome: 'Verdi', squadra: 'Juventus', ruolo: 'D', valore: '18' },
  { id: '4', nome: 'Esposito', squadra: 'Napoli', ruolo: 'D', valore: '11' },
  { id: '5', nome: 'Russo', squadra: 'Roma', ruolo: 'C', valore: '24' },
  { id: '6', nome: 'Ferrari', squadra: 'Atalanta', ruolo: 'C', valore: '15' },
  { id: '7', nome: 'Conti', squadra: 'Lazio', ruolo: 'A', valore: '38' },
  { id: '8', nome: 'Gallo', squadra: 'Fiorentina', ruolo: 'A', valore: '26' },
];

export const PLAYERS_IMPORT_TEMPLATE_FILENAME = 'falasta-calciatori-esempio.csv';

export const PLAYERS_IMPORT_TEMPLATE_MIME = 'text/csv;charset=utf-8';

/** Punto e virgola: è il separatore che Excel italiano scrive e rilegge. */
const TEMPLATE_DELIMITER = ';';

/** CRLF: il fine riga di RFC 4180, quello che Excel scrive esportando. */
const TEMPLATE_NEWLINE = '\r\n';

/**
 * BOM UTF-8.
 *
 * Senza, Excel su Windows apre il file come latin-1 e un nome accentato arriva
 * storto già nella riga di esempio. Il lettore dell'import lo scarta, quindi
 * costa nulla a chi ricarica il file senza mai aprirlo.
 */
const TEMPLATE_BOM = '\u{FEFF}';

/**
 * Quota il campo se contiene un carattere che cambierebbe la struttura.
 *
 * Non solo il separatore scelto: virgola e tabulazione sono gli altri due
 * candidati che il lettore conta per dedurre il separatore, e una virgola
 * lasciata nuda in un nome basterebbe a fargli scegliere quello sbagliato.
 */
function csvField(value: string): string {
  return /[";,\t\r\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

/**
 * Compone il CSV di esempio da scaricare prima dell'import.
 *
 * Vive nei contratti, non nel client: intestazioni, ruoli e limiti sono già qui,
 * e un modello tenuto come file statico accanto alla pagina divergerebbe dal
 * validatore alla prima colonna nuova senza che nulla se ne accorga. Un test in
 * `@falasta/excel` lo ridà in pasto al parser vero, così il file offerto in
 * download è per costruzione un file che l'import accetta.
 */
export function buildPlayersImportTemplateCsv(): string {
  const lines = [
    PLAYERS_IMPORT_COLUMNS.map(csvField).join(TEMPLATE_DELIMITER),
    ...PLAYERS_IMPORT_TEMPLATE_ROWS.map((row) =>
      PLAYERS_IMPORT_COLUMNS.map((column) => csvField(row[column])).join(TEMPLATE_DELIMITER),
    ),
  ];

  return `${TEMPLATE_BOM}${lines.join(TEMPLATE_NEWLINE)}${TEMPLATE_NEWLINE}`;
}

// ---------------------------------------------------------------------------
// Esito dell'import
// ---------------------------------------------------------------------------

/**
 * Errore riferito a una singola cella del file.
 *
 * `row` è il numero di riga **come lo vede l'utente in Excel**: l'intestazione è
 * la riga 1 e i dati partono dalla 2. `row`/`column` sono `null` quando il
 * problema riguarda il file nel suo insieme (formato illeggibile, foglio vuoto).
 */
export interface PlayersImportIssue {
  readonly row: number | null;
  readonly column: string | null;
  readonly message: string;
}

export interface PlayersImportRejected {
  readonly valid: false;
  readonly errors: readonly PlayersImportIssue[];
  /** `true` se l'elenco è stato troncato a `PLAYERS_IMPORT_MAX_ISSUES`. */
  readonly truncated: boolean;
}

export interface PlayersImportAccepted {
  readonly valid: true;
  /** Calciatori scritti. */
  readonly imported: number;
  /** Calciatori rimossi da un import precedente della stessa asta. */
  readonly replaced: number;
  readonly counts: PlayerRoleCounts;
}

/**
 * Risposta dell'import: **è un esito, non un errore**.
 *
 * Un file rifiutato non è un uso scorretto dell'API ma un risultato che la UI
 * mostra come tabella di errori; per questo non usa l'inviluppo `{ error: ... }`
 * comune al resto dell'API. Lo stato HTTP resta 422, così un client che guarda
 * solo il codice di stato vede comunque un fallimento.
 */
export type PlayersImportResponse = PlayersImportAccepted | PlayersImportRejected;

// ---------------------------------------------------------------------------
// Lettura della rosa
// ---------------------------------------------------------------------------

export interface PlayerSummary {
  readonly id: string;
  /** Colonna `id` del file caricato dall'amministratore. */
  readonly externalId: string;
  readonly name: string;
  readonly team: string;
  readonly role: PlayerRoleValue;
  /** Quotazione informativa: **non** è il prezzo di partenza della chiamata. */
  readonly value: number;
}

export interface PlayersListResponse {
  readonly players: readonly PlayerSummary[];
  readonly total: number;
  readonly counts: PlayerRoleCounts;
}

export const listPlayersQuerySchema = z.object({
  role: playerRoleSchema.optional(),
});

export type ListPlayersQuery = z.input<typeof listPlayersQuerySchema>;
